import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Table, Space, Modal, Row, Col, Tooltip, Input, Tag, DatePicker, Form, notification, Dropdown, Menu, Select } from 'antd';
import { EditOutlined, DeleteOutlined, ReloadOutlined, PlusCircleOutlined, EyeOutlined, CopyOutlined, CopyOutlined as DuplicateOutlined, MoreOutlined, FilterOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isEmpty, debounce } from 'underscore';
import dayjs from 'dayjs';
import 'dayjs/locale/lt';
import 'moment/locale/lt';
import locale from 'antd/es/date-picker/locale/lt_LT';

import { AppDispatch, CombinedStates } from '../../core/types';
import { Survey } from '../../core/survey/types';
import { School } from '../../core/school/types';
import { getPremadeSurveys, createSurvey, duplicateSurvey, deleteSurvey, createSurveyFromPremade } from '../../core/survey/surveyApi';
import { loadSinglePremadeSurvey } from '../../core/survey/surveySlice';
import { getSchools } from '../../core/school/schoolApi';

const SurveysListingPremadeContent: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [filterForm] = Form.useForm();
    const [selectedSchools, setSelectedSchools] = useState<number[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const {
        premadeSurveys,
        isFetching,
        isSurveyCreated,
    } = useSelector((state:CombinedStates) => state.survey);

    const {
        schools,
        isFetching: isSchoolsFetching,
    } = useSelector((state:CombinedStates) => state.school);

    const {
        userData,
    } = useSelector((state:CombinedStates) => state.user);

    // Load schools if user role is 5 and schools are empty
    useEffect(() => {
        if (userData?.role_id === 5 && isEmpty(schools)) {
            dispatch(getSchools({}));
        }
    }, [dispatch, userData?.role_id, schools]);

    const handleCreate = () => {
        form
            .validateFields()
            .then(values => {
                dispatch(createSurvey({
                    ...values,
                    active_from: values.active_from ? values.active_from.format('YYYY-MM-DD HH:mm:ss') : null,
                    active_until: values.active_until ? values.active_until.format('YYYY-MM-DD HH:mm:ss') : null,
                    is_premade: 1,
                }));
                setIsModalVisible(false);
                form.resetFields();
            })
            .catch(info => {
                console.log('Validacijos klaida:', info);
            });
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const handleFilterCancel = () => {
        setIsFilterModalVisible(false);
        filterForm.resetFields();
        setSelectedSchools([]);
    };

    const handleFilterApply = () => {
        filterForm
            .validateFields()
            .then(values => {
                const filters = {
                    ...values,
                    school_ids: selectedSchools.length > 0 ? selectedSchools : undefined,
                    is_premade: 1,
                };
                setCurrentPage(1);
                dispatch(getPremadeSurveys(filters));
                setIsFilterModalVisible(false);
            })
            .catch(info => {
                console.log('Filtro validacijos klaida:', info);
            });
    };

    const handleFilterReset = () => {
        setSelectedSchools([]);
        filterForm.resetFields();
        setCurrentPage(1);
        dispatch(getPremadeSurveys({}));
        setIsFilterModalVisible(false);
    };

    const debouncedSearch = useCallback(
        debounce((value: string) => {
            if (value.trim() !== '') {
                const filters: { search: string; school_ids?: number[] } = {
                    search: value,
                };
                if (selectedSchools.length > 0) {
                    filters.school_ids = selectedSchools;
                }
                dispatch(getPremadeSurveys(filters));
            }
        }, 1000),
        [selectedSchools],
    );

    // Trigger fetch on component mount
    useEffect(() => {
        if (isEmpty(premadeSurveys)) {
            dispatch(getPremadeSurveys({}));
        }
    }, []);

    useEffect(() => {
        if (isSurveyCreated === true) {
            navigate('/apklausa/redagavimas');
        }
    }, [isSurveyCreated]);

    const doSearch = (term: string) => {
        setSearchTerm(term);

        const trimmed = term.trim();

        if (trimmed === '') {
            // Čia tik tada, kai prieš tai buvo kažkas įvesta
            const filters: { school_ids?: number[] } = {};
            if (selectedSchools.length > 0) {
                filters.school_ids = selectedSchools;
            }
            setCurrentPage(1);
            dispatch(getPremadeSurveys(filters));
        } else {
            setCurrentPage(1);
            debouncedSearch(term);
        }
    };

    const handleEdit = (id: number) => {
        dispatch(loadSinglePremadeSurvey(id));
        navigate('/apklausa/redagavimas');
        // Navigate to edit form or open modal
    };

    const handleDelete = (id: number) => {
        Modal.confirm({
            title: 'Patvirtinkite veiksmą',
            content: 'Ar tikrai norite ištrinti šią apklausą?',
            okText: 'Taip, ištrinti',
            cancelText: 'Atšaukti',
            okType: 'danger',
            onOk() {
                dispatch(deleteSurvey({ surveyId: id }));
            },
        });
    };

    const handleDuplicate = (id: number) => {
        Modal.confirm({
            title: 'Patvirtinkite veiksmą',
            content: 'Ar tikrai norite sukurti apklausos kopiją? Nauja apklausa bus sukurta su visais klausimais, bet be užpildytų atsakymų.',
            okText: 'Taip, sukurti',
            cancelText: 'Atšaukti',
            onOk() {
                dispatch(duplicateSurvey({ surveyId: id }));
            },
        });
    };

    const handleCreateFromPremade = (id: number) => {
        Modal.confirm({
            title: 'Patvirtinkite veiksmą',
            content: 'Sukurti naują apklausą iš šios paruoštos apklausos?',
            okText: 'Taip, sukurti',
            cancelText: 'Atšaukti',
            onOk: () => dispatch(createSurveyFromPremade({ parent_survey_id: id })).unwrap().then(() => { navigate('/apklausos'); }),
        });
    };

    const handleCopyUrl = (slug: string) => {
        const surveyUrl = `${window.location.origin}/apklausa/pildymas/${slug}`;
        navigator.clipboard.writeText(surveyUrl).then(() => {
            notification.success({
                message: 'URL nukopijuotas',
                description: 'Apklausos adresas sėkmingai nukopijuotas į iškarpinę.',
                placement: 'topRight',
            });
        }).catch(() => {
            notification.error({
                message: 'Klaida',
                description: 'Nepavyko nukopijuoti URL. Bandykite dar kartą.',
                placement: 'topRight',
            });
        });
    };

    return (
        <>
            <Card style={{ marginTop: '-20px' }}>
                <Row className="listing-table-title" justify="space-between" align="middle">
                    <Col>
                        <h3 style={{ marginBottom: 0 }}>Paruoštos apklausos</h3>
                    </Col>
                    <Col>
                        <Space>
                            <Input
                                placeholder="Paieška..."
                                value={searchTerm}
                                onChange={(e) => doSearch(e.target.value)} />
                            {userData?.role_id !== 5 && (
                                <Tooltip title="Filtruoti apklausas">
                                    <Button
                                        icon={<FilterOutlined />}
                                        onClick={() => setIsFilterModalVisible(true)} />
                                </Tooltip>
                            )}
                            <Tooltip title="Atnaujinti sąrašą">
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={() => {
                                        dispatch(getPremadeSurveys({}));
                                        setSearchTerm('');
                                        setSelectedSchools([]);
                                        setCurrentPage(1);
                                        filterForm.resetFields();
                                    }} />
                            </Tooltip>
                            {userData?.role_id !== 5 && (
                                <Button loading={isFetching} icon={<PlusCircleOutlined />} type="primary" onClick={() => setIsModalVisible(true)}>
                                    Sukurti naują apklausą
                                </Button>
                            )}
                        </Space>
                    </Col>
                </Row>
                <Table
                    dataSource={premadeSurveys}
                    loading={isFetching}
                    rowKey="id"
                    pagination={{
                        current: currentPage,
                        pageSize,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} iš ${total} įrašų`,
                        onChange: (page, size) => {
                            setCurrentPage(page);
                            setPageSize(size || 10);
                        },
                    }}
                    columns={[
                        {
                            title: 'Nr. (ID)',
                            dataIndex: 'id',
                            key: 'nr',
                            render: (text, record, index) => `${(currentPage - 1) * pageSize + index + 1} (${record.id})`,
                        },
                        {
                            title: 'Pavadinimas',
                            dataIndex: 'title',
                            key: 'title',
                        },
                        {
                            title: 'Mokykla',
                            key: 'school_info',
                            render: (record: Survey) => {
                                if (record.school_info) {
                                    return (
                                        <Tag color="cyan">
                                            {record.school_info.name}
                                        </Tag>
                                    );
                                }
                                return <span style={{ color: '#999' }}>-</span>;
                            },
                        },
                        {
                            title: 'Apklausos pradžia',
                            dataIndex: 'active_from',
                            key: 'active_from',
                            render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm'),
                        },
                        {
                            title: 'Apklausos pabaiga',
                            dataIndex: 'active_until',
                            key: 'active_until',
                            render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm'),
                        },
                        {
                            title: 'Būsena',
                            key: 'status',
                            render: (record: Survey) => {
                                switch (record?.status) {
                                case 0:
                                    return <Tag color="gold">Juodraštis</Tag>;
                                case 1:
                                    return <Tag color="green">Aktyvi</Tag>;
                                case 2:
                                    return <Tag color="red">Užbaigta</Tag>;
                                case 3:
                                    return <Tag color="red">Sustabdyta</Tag>;
                                default:
                                    return <Tag color="default">Nežinoma</Tag>;
                                }
                            },
                        },
                        {
                            title: 'Klausimų',
                            key: 'questions_count',
                            render: (record: Survey) => (
                                <Tag color="purple">
                                    {record.questions?.filter(q => q.type !== 'paragraph').length || 0}
                                </Tag>
                            ),
                        },
                        {
                            title: 'Užpildė',
                            dataIndex: 'submissions_count',
                            key: 'submissions_count',
                            render: (count: number) => (
                                <Tag color="blue">
                                    {count || 0}
                                </Tag>
                            ),
                        },
                        {
                            title: 'Veiksmai',
                            key: 'actions',
                            width: 80,
                            render: (_text, record) => {
                                const menuItems = [
                                    {
                                        key: 'edit',
                                        icon: <EditOutlined />,
                                        label: 'Redaguoti',
                                        onClick: () => handleEdit(record.id),
                                    },
                                    {
                                        key: 'open',
                                        icon: <EyeOutlined />,
                                        label: 'Atidaryti',
                                        onClick: () => window.open(`/apklausa/pildymas/${record.slug}`, '_blank'),
                                    },
                                    {
                                        key: 'copy-url',
                                        icon: <CopyOutlined />,
                                        label: 'Kopijuoti URL',
                                        onClick: () => handleCopyUrl(record.slug),
                                    },
                                    {
                                        key: 'create-from-premade',
                                        icon: <PlusCircleOutlined />,
                                        label: 'Sukurti apklausą paruoštos apklausos pagrindu',
                                        onClick: () => handleCreateFromPremade(record.id),
                                    },
                                ];

                                // Pridėti kopijavimo meniu punktą tik jei role_id != 5
                                if (userData?.role_id !== 5) {
                                    menuItems.push({
                                        key: 'duplicate',
                                        icon: <DuplicateOutlined />,
                                        label: 'Kopijuoti apklausą',
                                        onClick: () => handleDuplicate(record.id),
                                    });
                                }

                                // Pridėti šalinimo meniu punktą tik jei statusas yra 0 (juodraštis)
                                if (record?.status === 0) {
                                    menuItems.push({
                                        key: 'delete',
                                        icon: <DeleteOutlined />,
                                        label: 'Pašalinti',
                                        onClick: () => handleDelete(record.id),
                                    });
                                }

                                const menu = (
                                    <Menu items={menuItems} />
                                );

                                return (
                                    <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
                                        <Button
                                            size="small"
                                            style={{ padding: '4px 8px' }}>
                                            Meniu <MoreOutlined />
                                        </Button>
                                    </Dropdown>
                                );
                            },
                        },
                    ]} />
            </Card>
            <Modal
                title="Sukurti naują apklausą"
                visible={isModalVisible}
                onOk={handleCreate}
                onCancel={handleCancel}
                okText="Sukurti"
                cancelText="Atšaukti">
                <Form layout="vertical" form={form}>
                    <Form.Item
                        label="Pavadinimas"
                        name="title"
                        rules={[{ required: true, message: 'Įveskite pavadinimą' }]}>
                        <Input placeholder="Apklausos pavadinimas" />
                    </Form.Item>

                    <Form.Item
                        label="Aprašymas"
                        name="description">
                        <Input.TextArea placeholder="Apklausos aprašymas" rows={4} />
                    </Form.Item>

                    <Form.Item
                        label="Apklausa aktyvi nuo"
                        name="active_from"
                        rules={[{ required: true, message: 'Pasirinkite pradžios datą' }]}>
                        <DatePicker
                            locale={locale}
                            showTime
                            style={{ width: '100%' }}
                            format="YYYY-MM-DD HH:mm" />
                    </Form.Item>

                    <Form.Item
                        label="Apklausa aktyvi iki"
                        name="active_until"
                        rules={[{ required: true, message: 'Pasirinkite pabaigos datą' }]}>
                        <DatePicker
                            locale={locale}
                            showTime
                            style={{ width: '100%' }}
                            format="YYYY-MM-DD HH:mm" />
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                title="Filtruoti apklausas"
                visible={isFilterModalVisible}
                onCancel={handleFilterCancel}
                footer={[
                    <Button key="reset" onClick={handleFilterReset}>
                        Išvalyti filtrus
                    </Button>,
                    <Button key="submit" type="primary" onClick={handleFilterApply}>
                        Filtruoti
                    </Button>,
                ]}>
                <Form layout="vertical" form={filterForm}>
                    <Form.Item
                        label="Mokyklos"
                        name="school_ids">
                        <Select
                            mode="multiple"
                            placeholder="Pasirinkite mokyklas"
                            loading={isSchoolsFetching}
                            value={selectedSchools}
                            onChange={setSelectedSchools}
                            showSearch
                            filterOption={(input, option) => (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())}>
                            {schools.map((school: School) => (
                                <Select.Option key={school.id} value={school.id}>
                                    {school.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default SurveysListingPremadeContent;
